import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { userGrowth, satisfactionTrend, feedbackThemes, complaintStats, economicImpact } from '../utils/mockData';
import { CheckCircleIcon, UserIcon, BuildingIcon, ClockIcon, BanknoteIcon } from 'lucide-react';
const CitizenPanel = ({
  filters
}) => {
  return <div className="w-80 bg-slate-800 rounded-lg p-4 flex flex-col gap-4" data-id="element-39">
      <h2 className="text-xl font-semibold" data-id="element-40">Citizen Impact</h2>
      {/* User Growth */}
      <div className="bg-slate-700 p-3 rounded-lg" data-id="element-41">
        <h3 className="text-sm font-semibold mb-3" data-id="element-42">User Growth</h3>
        <div className="h-48" data-id="element-43">
          <ResponsiveContainer width="100%" height="100%" data-id="element-44">
            <LineChart data={userGrowth} margin={{
            top: 5,
            right: 5,
            left: 0,
            bottom: 5
          }} data-id="element-45">
              <XAxis dataKey="month" tick={{
              fontSize: 10
            }} data-id="element-46" />
              <YAxis hide data-id="element-47" />
              <Tooltip formatter={value => [value.toLocaleString(), 'Users']} labelFormatter={label => label} data-id="element-48" />
              <Line type="monotone" dataKey="citizens" stroke="#3b82f6" strokeWidth={2} dot={false} name="Citizens" data-id="element-49" />
              <Line type="monotone" dataKey="businesses" stroke="#10b981" strokeWidth={2} dot={false} name="Businesses" data-id="element-50" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Satisfaction Trend */}
      <div className="bg-slate-700 p-3 rounded-lg" data-id="element-51">
        <h3 className="text-sm font-semibold mb-3" data-id="element-52">Citizen Satisfaction</h3>
        <div className="h-32" data-id="element-53">
          <ResponsiveContainer width="100%" height="100%" data-id="element-54">
            <LineChart data={satisfactionTrend} margin={{
            top: 5,
            right: 5,
            left: 0,
            bottom: 5
          }} data-id="element-55">
              <XAxis dataKey="month" tick={{
              fontSize: 10
            }} data-id="element-56" />
              <YAxis hide domain={[0.7, 0.9]} data-id="element-57" />
              <Tooltip formatter={value => [`${(value * 100).toFixed(1)}%`, 'Satisfaction']} labelFormatter={label => label} data-id="element-58" />
              <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} data-id="element-59" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Feedback Themes */}
      <div className="bg-slate-700 p-3 rounded-lg" data-id="element-60">
        <h3 className="text-sm font-semibold mb-2" data-id="element-61">Key Feedback Themes</h3>
        <div className="grid grid-cols-2 gap-2" data-id="element-62">
          {feedbackThemes.map((theme, index) => <div key={index} className={`text-xs p-2 rounded-lg border ${theme.sentiment === 'positive' ? 'border-green-500 bg-green-500 bg-opacity-10' : 'border-red-500 bg-red-500 bg-opacity-10'}`} style={{
          fontSize: `${Math.min(Math.max(theme.weight / 20, 0.7), 1)}rem`,
          opacity: Math.min(Math.max(theme.weight / 100, 0.7), 1)
        }} data-id="element-63">
              {theme.theme}
            </div>)}
        </div>
      </div>
      {/* Complaints */}
      <div className="bg-slate-700 p-3 rounded-lg" data-id="element-64">
        <h3 className="text-sm font-semibold mb-2" data-id="element-65">Complaints & Appeals</h3>
        <div className="flex justify-between text-xs mb-2" data-id="element-66">
          <div data-id="element-67">Open Complaints</div>
          <div data-id="element-68">{complaintStats.open.toLocaleString()}</div>
        </div>
        <div className="flex justify-between text-xs mb-2" data-id="element-69">
          <div data-id="element-70">Resolution Rate</div>
          <div className="text-green-400" data-id="element-71">
            {(complaintStats.resolutionRate * 100).toFixed(1)}%
          </div>
        </div>
      </div>
      {/* Economic Impact */}
      <div className="bg-slate-700 p-3 rounded-lg" data-id="element-72">
        <h3 className="text-sm font-semibold mb-2" data-id="element-73">Economic Impact</h3>
        <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-74">
          <div className="flex flex-col items-center" data-id="element-75">
            <BuildingIcon size={20} className="text-blue-400 mb-1" data-id="element-76" />
            <div className="font-semibold" data-id="element-77">
              {economicImpact.newBusinesses.toLocaleString()}
            </div>
            <div className="text-slate-400 text-center" data-id="element-78">New Businesses</div>
          </div>
          <div className="flex flex-col items-center" data-id="element-79">
            <UserIcon size={20} className="text-blue-400 mb-1" data-id="element-80" />
            <div className="font-semibold" data-id="element-81">
              {economicImpact.landTitles.toLocaleString()}
            </div>
            <div className="text-slate-400 text-center" data-id="element-82">Land Titles</div>
          </div>
          <div className="flex flex-col items-center" data-id="element-83">
            <ClockIcon size={20} className="text-blue-400 mb-1" data-id="element-84" />
            <div className="font-semibold" data-id="element-85">
              {(economicImpact.timeSaved / 1000000).toFixed(1)}M
            </div>
            <div className="text-slate-400 text-center" data-id="element-86">Hours Saved</div>
          </div>
          <div className="flex flex-col items-center" data-id="element-87">
            <BanknoteIcon size={20} className="text-blue-400 mb-1" data-id="element-88" />
            <div className="font-semibold" data-id="element-89">
              ETB {(economicImpact.costSaved / 1000000).toFixed(0)}M
            </div>
            <div className="text-slate-400 text-center" data-id="element-90">Cost Saved</div>
          </div>
        </div>
      </div>
    </div>;
};
export default CitizenPanel;